import { useEffect, useMemo, useState } from "react";
import { Download } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { PageTitle } from "./AdminAnalytics";
import { LeadRow } from "./LeadRow";

const FILTERS = [["all", "Tutti"], ["due", "Da ricontattare"], ["purchased", "Acquistati"]];

export default function AdminLeads() {
  const [leads, setLeads] = useState(null);
  const [filter, setFilter] = useState("all");
  const [openId, setOpenId] = useState(null);
  const load = () => api.get("/admin/leads").then((r) => setLeads(r.data));
  useEffect(() => { load(); }, []);

  const counts = useMemo(() => ({
    all: leads?.length || 0,
    due: leads?.filter((l) => l.reminder_due).length || 0,
    purchased: leads?.filter((l) => l.status === "acquistato" || l.cart_clicked).length || 0,
  }), [leads]);

  const visible = (leads || []).filter((l) =>
    filter === "due" ? l.reminder_due : filter === "purchased" ? l.status === "acquistato" || l.cart_clicked : true);

  const patch = (lead) => setLeads((ls) => ls.map((l) => (l.id === lead.id ? lead : l)));

  const remove = async (l) => {
    if (!window.confirm(`Eliminare il contatto di ${l.name}?`)) return;
    await api.delete(`/admin/leads/${l.id}`);
    toast.success("Contatto eliminato");
    load();
  };

  const exportCsv = async () => {
    try {
      const { data } = await api.get("/admin/leads/export", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = Object.assign(document.createElement("a"), { href: url, download: `fineskin-clienti-${new Date().toISOString().slice(0, 10)}.csv` });
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Esportazione completata");
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!leads) return <p className="text-sm text-[#8C8275]">Caricamento…</p>;

  return (
    <div data-testid="admin-leads-page">
      <PageTitle eyebrow="Contatti raccolti" title="Clienti">
        <div className="flex items-center gap-4">
          <p className="text-sm text-[#8C8275]" data-testid="leads-count">{leads.length} contatti</p>
          <button data-testid="leads-export-btn" className="fs-btn-ghost" onClick={exportCsv} disabled={!leads.length}>
            <Download size={14} strokeWidth={1.5} /> Esporta CSV
          </button>
        </div>
      </PageTitle>
      <div className="inline-flex border border-[#E2DACF] bg-white p-1 mb-4" role="tablist">
        {FILTERS.map(([k, label]) => (
          <button key={k} role="tab" aria-selected={filter === k} data-testid={`leads-filter-${k}`} onClick={() => setFilter(k)}
            className={`px-4 py-2 text-xs tracking-[0.12em] uppercase transition-colors ${filter === k ? "bg-[#1A1A18] text-[#FAF8F5]" : "text-[#595652] hover:text-[#1A1A18]"}`}>
            {label} <span className={`ml-1 tabular-nums ${filter === k ? "text-[#C5A059]" : "text-[#C2B8A8]"}`}>{counts[k]}</span>
          </button>
        ))}
      </div>
      <div className="bg-white border border-[#E2DACF] overflow-x-auto">
        {!visible.length && (
          <p className="p-6 text-sm text-[#C2B8A8]" data-testid="leads-empty">
            {filter === "due" ? "Nessun cliente da ricontattare: tutti hanno acquistato o hanno già ricevuto il promemoria."
              : filter === "purchased" ? "Nessun acquisto registrato ancora."
              : "Nessun contatto ancora. I clienti compaiono qui quando richiedono la routine via WhatsApp o email."}
          </p>
        )}
        {visible.length > 0 && (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-[#8C8275]">
              {["Cliente e data", "Contatto", "Pelle", "Routine", "Stato", ""].map((h) => <th key={h} className="font-medium px-4 py-3">{h}</th>)}
            </tr></thead>
            <tbody>
              {visible.map((l) => (
                <LeadRow key={l.id} lead={l} open={openId === l.id} onToggle={() => setOpenId(openId === l.id ? null : l.id)} onRemove={() => remove(l)} onPatch={patch} />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
